'use client'

import { useState } from 'react'
import {
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  FileText,
  Github,
  Shield,
  Terminal,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { track } from '@/lib/analytics/track'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'

interface PromptCollapsibleProps {
  slug: string
  content: string
  author?: { name: string; url?: string; avatarUrl?: string }
  compatibleModels?: string[]
  securityRating?: string
  tags?: string[]
  version?: string
  installCommand?: string
  sourceUrl?: string
}

export function PromptCollapsible({
  slug,
  content,
  author,
  compatibleModels,
  securityRating,
  tags,
  version,
  installCommand,
  sourceUrl,
}: PromptCollapsibleProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [installCopied, setInstallCopied] = useState(false)

  const lines = content.split('\n')
  const lineCount = lines.length
  const preview = lines.slice(0, 3).join('\n')

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleInstallCopy = async () => {
    if (!installCommand) return
    await navigator.clipboard.writeText(installCommand)
    setInstallCopied(true)
    setTimeout(() => setInstallCopied(false), 2000)
  }

  const hasMeta =
    author || compatibleModels?.length || securityRating || tags?.length

  return (
    <div className="mt-12 rounded-xl border bg-muted/30 p-6">
      <Collapsible open={isOpen} onOpenChange={(open) => {
        setIsOpen(open)
        if (open) track('skill_prompt_expand', { slug })
      }}>
        {/* Header */}
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold">Full Skill</h3>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          The original AI prompt behind this skill. Copy it to use in any AI
          agent.
        </p>

        {/* Preview (visible when collapsed) */}
        {!isOpen && (
          <div className="relative mt-4 overflow-hidden rounded-lg bg-muted p-4">
            <pre className="font-mono text-sm text-muted-foreground whitespace-pre-wrap">
              {preview}
            </pre>
            <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-muted to-transparent" />
          </div>
        )}

        {/* Expanded content */}
        <CollapsibleContent>
          <div className="mt-4 max-h-[600px] overflow-y-auto rounded-lg bg-muted p-4">
            <pre className="font-mono text-sm text-muted-foreground whitespace-pre-wrap">
              {content}
            </pre>
          </div>
        </CollapsibleContent>

        {/* Actions */}
        <div className="mt-4 flex items-center justify-between">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {isOpen ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Collapse
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Show full skill ({lineCount} lines)
                </>
              )}
            </button>
          </CollapsibleTrigger>

          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5 text-green-600" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copy Full Skill
              </>
            )}
          </button>
        </div>
      </Collapsible>

      {/* Developer meta — author, compatibility, security, tags */}
      {hasMeta && (
        <div className="mt-5 border-t pt-5">
          <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
            {/* Actions: install command, view source */}
            {(installCommand || sourceUrl) && (
              <div className="flex flex-wrap gap-2">
                {installCommand && (
                  <button
                    type="button"
                    onClick={handleInstallCopy}
                    className="inline-flex items-center gap-2 rounded-lg border bg-muted/50 px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted"
                  >
                    {installCopied ? (
                      <Check className="h-3.5 w-3.5 shrink-0 text-green-600" />
                    ) : (
                      <Terminal className="h-3.5 w-3.5 shrink-0" />
                    )}
                    <span className="truncate">
                      {installCopied ? 'Copied!' : installCommand}
                    </span>
                  </button>
                )}
                {sourceUrl && (
                  <a
                    href={sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted"
                  >
                    <Github className="h-3.5 w-3.5" />
                    View on GitHub
                  </a>
                )}
              </div>
            )}

            {/* Meta info */}
            <dl className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
              {author && (
                <div className="flex items-center gap-2">
                  <dt className="text-muted-foreground">Author</dt>
                  <dd className="flex items-center gap-1.5">
                    {author.avatarUrl ? (
                      /* eslint-disable-next-line @next/next/no-img-element */
                      <img
                        src={author.avatarUrl}
                        alt={author.name}
                        width={18}
                        height={18}
                        className="rounded-full"
                      />
                    ) : (
                      <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-muted text-[10px] font-medium">
                        {author.name[0]}
                      </span>
                    )}
                    {author.url ? (
                      <a
                        href={author.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium hover:underline"
                      >
                        {author.name}
                      </a>
                    ) : (
                      <span className="font-medium">{author.name}</span>
                    )}
                  </dd>
                </div>
              )}
              {compatibleModels && compatibleModels.length > 0 && (
                <div className="flex items-center gap-2">
                  <dt className="text-muted-foreground">Compatible with</dt>
                  <dd>{compatibleModels.join(', ')}</dd>
                </div>
              )}
              {version && (
                <div className="flex items-center gap-2">
                  <dt className="text-muted-foreground">Version</dt>
                  <dd className="font-medium">v{version}</dd>
                </div>
              )}
              {securityRating && (
                <div className="flex items-center gap-2">
                  <dt className="text-muted-foreground">Security</dt>
                  <dd className="flex items-center gap-1">
                    <Shield className="h-3.5 w-3.5 text-green-600" />
                    {securityRating}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Tags */}
          {tags && tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {tags.map(tag => (
                <Badge key={tag} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
